import React from 'react';
import './SegmentedToggle.css';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Disabled but still rendered (a value that does not apply stays visible, never hidden) */
  disabled?: boolean;
  title?: string;
  /** Optional leading icon, rendered before the label inside the pill. */
  icon?: React.ReactNode;
}

interface SegmentedToggleProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel?: string;
  className?: string;
  /** Disables every pill (e.g. while the backing setting is loading/saving) */
  disabled?: boolean;
}

/**
 * Pill segmented control — the by-card / by-trader toggle shared by the Trade Alerts and
 * Activity pages. Extracted from the inline Trade Alerts markup so both surfaces render the
 * same control (branding-gradient active state) instead of each maintaining its own copy.
 */
export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  disabled = false,
}: SegmentedToggleProps<T>) {
  return (
    <div className={`segmented-toggle ${className || ''}`} role="group" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`segmented-toggle__pill ${value === opt.value ? 'active' : ''}`}
          aria-pressed={value === opt.value}
          aria-disabled={opt.disabled || undefined}
          disabled={disabled || opt.disabled}
          title={opt.title}
          onClick={() => onChange(opt.value)}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default SegmentedToggle;
